import { useGLTF, useAnimations } from '@react-three/drei'
import { useFrame, useThree } from '@react-three/fiber'
import React, { useEffect, useMemo, useState, useRef } from 'react'
import * as THREE from 'three'
import { RunCalc } from '../RunCalc/RunCalc.tsx'
import holographicVertexShader from '../../shaders/holographicTracer/vertex.glsl?raw'
import holographicFragmentShader from '../../shaders/holographicTracer/fragment.glsl?raw'
import ThreeDInformation from '../3DInformation/3DInformation'
// @ts-ignore
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js'

interface Props {
  distance: number
  lane: number
  time: number
  isPlay: boolean
  setPlay: React.Dispatch<React.SetStateAction<boolean>>
  cameraLocked: boolean
  timePerKm: string,
}

interface Checkpoint {
  position: [number, number, number]
  text: string
  time: number
  startDistance: number
}

const Runner = ({distance, lane, time, isPlay, timePerKm, setPlay, cameraLocked }: Props): React.JSX.Element => {
  const { gl } = useThree()

  const ktx2Loader = useMemo(() => {
    const loader = new KTX2Loader()
    loader.setTranscoderPath('/basis/')
    loader.detectSupport(gl)
    return loader
  }, [gl])

  const { scene, animations } = useGLTF(
    '/runner_web.glb',
    true,
    undefined,
    loader => {
      // @ts-ignore
      loader.setKTX2Loader(ktx2Loader)
    }
  )

  const { actions } = useAnimations(animations, scene)

  console.log(actions)

  const { camera } = useThree()

  const [positions, setPositions] = useState<[number, number, number]>([-4.20, 0.1, -3.7])
  const [rotations, setRotations] = useState<[number, number, number]>([-0.05, -1.65, 0])
  const [visibleCheckpoints, setVisibleCheckpoints] = useState<Checkpoint[]>([])

  const finishedRef = useRef(false)
  const cameraTarget = useRef(new THREE.Vector3())
  const cameraVelocity = useRef(new THREE.Vector3())
  const startTimeRef = useRef<number | null>(null)
  const tracerRef = useRef<THREE.Mesh>(null)
  const fadeOutRef = useRef(false)
  const fadeStartTimeRef = useRef<number | null>(null)
  const pathPointsRef = useRef<THREE.Vector3[]>([])

  // Préchargement du premier Text3D invisible
  const preloadFont = useMemo(() => {
    const mesh = new THREE.Mesh()
    mesh.visible = false
    return mesh
  }, [])

  const checkpoints = useMemo(() => {
    const points: Checkpoint[] = []
    for (let d = 100; d <= distance; d += 100) {
      const { position } = RunCalc({
        distance,
        lane,
        time,
        elapsedTime: (d / distance) * time,
        positionsStart: positions,
        rotationsStart: rotations,
      })
      points.push({
        position,
        text: d.toString(),
        time: (d / distance) * time,
        startDistance: d,
      })
    }
    return points
  }, [distance, lane, time])

  const material = useMemo(() => new THREE.MeshStandardMaterial({ color: '#5e64ff' }), [])
  
  useEffect(() => {
    scene.traverse((child) => {
      if (child.isMesh) child.material = material
    })
  }, [scene, material])

  // Shader holographique
  const tracerMaterial = useMemo(() => new THREE.ShaderMaterial({
    vertexShader: holographicVertexShader,
    fragmentShader: holographicFragmentShader,
    uniforms: {
      uTime: { value: 0 },
      uColor: { value: new THREE.Color('#5a6af5').convertSRGBToLinear() },
      uOpacity: { value: 1.0 },
    },
    transparent: true,
    side: THREE.DoubleSide,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  }), [])

  useEffect(() => {
    if (!actions) return
    const standingAction = actions["Standing"]
    const runAction = actions["Run"]
    const runChillAction = actions["RunChill-v2"]

    const [min, sec] = timePerKm.split(":").map(Number)
    const paceSeconds = min * 60 + sec
    const useChill = paceSeconds > 270

    if (isPlay) {
      standingAction?.fadeOut(0.5)
      const currentRun = useChill ? runChillAction : runAction
      if (currentRun) {
        currentRun.reset().fadeIn(0.5).play()
        currentRun.setLoop(THREE.LoopRepeat, Infinity)
      }
    } else {
      runAction?.fadeOut(0.5)
      runChillAction?.fadeOut(0.5)
      standingAction?.reset().fadeIn(0.5).play()
      standingAction?.setLoop(THREE.LoopRepeat, Infinity)
    }
  }, [isPlay, actions, timePerKm])



  useEffect(() => {
    if (!isPlay) {
      finishedRef.current = false
      fadeOutRef.current = false
      fadeStartTimeRef.current = null
      startTimeRef.current = null
      pathPointsRef.current = []
      setVisibleCheckpoints([])
      if (tracerRef.current) {
        tracerRef.current.geometry.dispose()
        tracerRef.current.geometry = new THREE.BufferGeometry()
        tracerMaterial.uniforms.uOpacity.value = 1
      }
    }
  }, [isPlay])

  useFrame(({ clock }) => {
    tracerMaterial.uniforms.uTime.value = clock.getElapsedTime()
    const elapsedGlobal = clock.getElapsedTime()

    if (!isPlay) return

    if (startTimeRef.current === null) startTimeRef.current = elapsedGlobal
    const localElapsed = elapsedGlobal - startTimeRef.current

    if (localElapsed >= time && !finishedRef.current) {
      finishedRef.current = true
      fadeOutRef.current = true
      setPlay(false)
      return
    }

    // Position coureur
    const { position, rotation, segment } = RunCalc({
      distance,
      lane,
      time,
      elapsedTime: localElapsed,
      positionsStart: positions,
      rotationsStart: rotations,
    }) as any
    setPositions(position)
    setRotations(rotation)

    const runnerPos = new THREE.Vector3(...position)
    const runnerRot = new THREE.Euler(...rotation)

    // Gestion des checkpoints visibles (file max 4)
    const speed = distance / time
    const totalDistance = Math.min(localElapsed * speed, distance)

    const nextCp = checkpoints.find(cp =>
      totalDistance >= cp.startDistance &&
      totalDistance < cp.startDistance + 400 &&
      !visibleCheckpoints.includes(cp)
    )
    if (nextCp) {
      const newVisible = [...visibleCheckpoints, nextCp]
      if (newVisible.length > 4) newVisible.shift()
      setVisibleCheckpoints(newVisible)
    }

    // Construction du tracer
    pathPointsRef.current.push(runnerPos.clone())
    let totalDist = 0
    const maxLength = 5.0
    for (let i = pathPointsRef.current.length - 1; i > 0; i--) {
      totalDist += pathPointsRef.current[i].distanceTo(pathPointsRef.current[i - 1])
      if (totalDist > maxLength) {
        pathPointsRef.current = pathPointsRef.current.slice(i)
        break
      }
    }
    if (pathPointsRef.current.length > 2 && tracerRef.current) {
      const curve = new THREE.CatmullRomCurve3(pathPointsRef.current)
      const geometry = new THREE.TubeGeometry(curve, 128, 0.05, 3, false)
      geometry.scale(1, 0.05, 1)
      geometry.translate(0, 0.09, 0)
      tracerRef.current.geometry.dispose()
      tracerRef.current.geometry = geometry
    }

    if (!cameraLocked) return

    const forward = new THREE.Vector3(0, 0, -1).applyEuler(runnerRot)
    const right = new THREE.Vector3(1, 0, 0).applyEuler(runnerRot)
    const targetPos = runnerPos.clone()
    switch (segment) {
      case 1: targetPos.add(forward.clone().multiplyScalar(-1.5)); break
      case 2: targetPos.add(right.clone().multiplyScalar(1.5)); break
      case 3: targetPos.add(forward.clone().multiplyScalar(1.5)); break
      case 4: targetPos.add(right.clone().multiplyScalar(-1.5)); break
    }
    targetPos.y += 0.5

    const delta = clock.getDelta()
    const smoothTime = 0.2
    const damping = 0.9

    // Position avec inertie
    const toTarget = targetPos.clone().sub(camera.position)
    cameraVelocity.current.add(toTarget.multiplyScalar(delta / smoothTime))
    cameraVelocity.current.multiplyScalar(damping)
    camera.position.add(cameraVelocity.current)

    // LookAtTarget séparé
    if (!cameraTarget.current) cameraTarget.current = runnerPos.clone()
    cameraTarget.current.lerp(runnerPos, 0.1) // interpolation douce du regard
    camera.lookAt(cameraTarget.current)
  })

  return (
    <>
      <ThreeDInformation
        key="preload"
        position={[0, 0, 0]}
        text="preload"
        time={0}
        size={0.01}
        opacity={0}
      />

      <primitive object={scene} scale={0.16} position={positions} rotation={rotations} />
      <mesh ref={tracerRef} material={tracerMaterial} />

      {visibleCheckpoints.map((cp) => (
        <ThreeDInformation
          key={cp.text}
          position={[cp.position[0], cp.position[1] + 0.8, cp.position[2]]}
          text={cp.text}
          time={cp.time}
          size={0.1}
          opacity={0.8}
        />
      ))}
    </>
  )
}

export default Runner
